import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../utils/auth';
import Sidebar from '../components/Sidebar';
import HighRiskFormsDetail from '../components/HighRiskFormsDetail';
import { ArrowLeft, FileText, Download, CheckCircle, XCircle, Edit, Clock, AlertTriangle, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getUser();
  const [ptw, setPtw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showSelectApproversModal, setShowSelectApproversModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // For PIC to select approvers
  const [areaManagerUsers, setAreaManagerUsers] = useState([]);
  const [sheUsers, setSheUsers] = useState([]);
  const [managementUsers, setManagementUsers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState({
    area_manager_id: '',
    she_id: '',
    management_id: ''
  });

  useEffect(() => {
    fetchPTW();
    fetchUsers();
  }, [id]);

  const fetchPTW = async () => {
    try {
      const res = await axios.get(`${API}/ptw/${id}`, { headers: getAuthHeaders() });
      setPtw(res.data);
    } catch (error) {
      console.error('Error fetching PTW:', error);
      toast.error('Gagal memuat detail PTW');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const [amRes, sheRes, mgmtRes] = await Promise.all([
        axios.get(`${API}/users?role=area_manager`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users?role=she`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users?role=management`, { headers: getAuthHeaders() })
      ]);
      setAreaManagerUsers(amRes.data);
      setSheUsers(sheRes.data);
      setManagementUsers(mgmtRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAction = async () => {
    if (!actionType) return;
    if ((actionType === 'revise' || actionType === 'closing_revise') && !actionNotes.trim()) {
      toast.error('Catatan revisi harus diisi');
      return;
    }

    setActionLoading(true);
    try {
      // Handle closing approval separately
      if (actionType === 'closing_approve' || actionType === 'closing_revise') {
        const action = actionType === 'closing_approve' ? 'approve' : 'revise';
        await axios.post(
          `${API}/ptw/${id}/closing-approval`,
          { action, notes: actionNotes },
          { headers: getAuthHeaders() }
        );
        toast.success(actionType === 'closing_approve' ? 'Closing berhasil diapprove!' : 'Closing diminta revisi');
      } else {
        await axios.post(
          `${API}/ptw/${id}/action`,
          { action: actionType, notes: actionNotes },
          { headers: getAuthHeaders() }
        );
        toast.success(`PTW berhasil ${actionType === 'approve' ? 'diapprove' : actionType === 'revise' ? 'diminta revisi' : 'ditolak'}`);
      }
      setShowActionModal(false);
      fetchPTW();
    } catch (error) {
      console.error('Error performing action:', error);
      toast.error(error.response?.data?.detail || 'Gagal melakukan aksi');
    } finally {
      setActionLoading(false);
      setActionNotes('');
      setActionType('');
    }
  };

    const handleSelectApprovers = async () => {
    if (!selectedApprovers.area_manager_id || !selectedApprovers.she_id) {
      toast.error('Area Manager dan SHE harus dipilih');
      return;
    }
     
    // 🔴 PERBAIKAN: Cek Amonia dari isolasi_utilitas, bukan high_risk_activities
    const hasAmonia = ptw?.isolasi_utilitas?.dilakukan && 
                      ptw?.isolasi_utilitas?.jenis_utilitas?.includes('Amonia');
    
    if (hasAmonia && !selectedApprovers.management_id) {
      toast.error('Amonia WAJIB memerlukan approval Management. Silakan pilih Management approver.');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/ptw/${id}/select-approvers`,
        selectedApprovers,
        { headers: getAuthHeaders() }
      );
      toast.success('Approvers berhasil dipilih!');
      setShowSelectApproversModal(false);
      fetchPTW();
    } catch (error) {
      console.error('Error selecting approvers:', error);
      toast.error(error.response?.data?.detail || 'Gagal memilih approvers');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/ptw/${id}/resubmit`, {}, { headers: getAuthHeaders() });
      toast.success('PTW berhasil di-resubmit ke approver!');
      fetchPTW();
    } catch (error) {
      console.error('Error resubmitting PTW:', error);
      toast.error(error.response?.data?.detail || 'Gagal resubmit PTW');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get(`${API}/ptw/${id}/export-pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PTW_${id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF berhasil didownload');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Gagal download PDF');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: <Edit size={16} /> },
      waiting_high_risk_forms: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock size={16} /> },
      waiting_pic: { color: 'bg-blue-100 text-blue-800', icon: <Clock size={16} /> },
      waiting_area_manager: { color: 'bg-blue-100 text-blue-800', icon: <Clock size={16} /> },
      waiting_she: { color: 'bg-blue-100 text-blue-800', icon: <Clock size={16} /> },
      waiting_management: { color: 'bg-blue-100 text-blue-800', icon: <Clock size={16} /> },
      needs_revision: { color: 'bg-orange-100 text-orange-800', icon: <AlertTriangle size={16} /> },
      approved: { color: 'bg-green-100 text-green-800', icon: <CheckCircle size={16} /> },
      waiting_closing: { color: 'bg-purple-100 text-purple-800', icon: <Clock size={16} /> },
      waiting_closing_approval: { color: 'bg-purple-100 text-purple-800', icon: <Clock size={16} /> },
      rejected: { color: 'bg-red-100 text-red-800', icon: <XCircle size={16} /> },
      closed: { color: 'bg-gray-100 text-gray-800', icon: <CheckCircle size={16} /> }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const label = status.replace(/_/g, ' ').toUpperCase();

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        {label}
      </span>
    );
  };

  // Permission flags
  const isCurrentApprover = ptw?.current_approver_id === user.id;
  const isRequester = ptw?.requester_id === user.id;
  const isPIC = ptw?.pic_id === user.id;
  
  // Revision mode - requester can edit but NOT change PIC
  const canEdit = isRequester && ptw?.status === 'needs_revision';
  const canResubmit = isRequester && ptw?.status === 'needs_revision';
  const canEditHighRiskFormsRevision = isRequester && ptw?.status === 'needs_revision' && (ptw?.high_risk_activities?.length > 0);
  
  // PIC select approvers - only when no approvers set yet
  const canSelectApprovers = isPIC && ptw?.status === 'waiting_pic' && !ptw?.area_manager_id;
  
  // High risk forms mode
  const canFillHighRiskForms = isRequester && ptw?.status === 'waiting_high_risk_forms';
  
  // Closing mode - vendor can submit closing when status is waiting_closing
  const canSubmitClosing = isRequester && ptw?.status === 'waiting_closing';
  
  // PIC can approve closing
  const canApproveClosing = isPIC && ptw?.status === 'waiting_closing_approval';
  
  // PDF only available after closed
  const canDownloadPDF = ptw?.status === 'closed';
  
  // Approver can only take action if they're current approver AND approvers are selected (for PIC)
  const canTakeApprovalAction = isCurrentApprover && ptw?.status !== 'needs_revision' && ptw?.status !== 'waiting_high_risk_forms' && ptw?.status !== 'waiting_closing' && ptw?.status !== 'waiting_closing_approval';

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ptw) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">PTW tidak ditemukan</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 lg:ml-64 overflow-auto pt-16 lg:pt-0">
        <div className="max-w-6xl mx-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
            >
              <ArrowLeft size={20} />
              Kembali
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Detail PTW</h1>
                <p className="text-sm text-gray-600 mt-1">ID: {ptw.id.substring(0, 8)}</p>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(ptw.status)}
                {canDownloadPDF && (
                  <button
                    onClick={handleDownloadPDF}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Download size={18} />
                    Download PDF
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - REVISION MODE */}
          {canEdit && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="mb-3">
                <p className="text-sm text-orange-800 font-medium">
                  ⚠️ PTW ini perlu direvisi. Silakan edit dan resubmit.
                </p>
                {ptw.revision_notes && (
                  <p className="text-sm text-orange-700 mt-2">
                    <strong>Catatan revisi:</strong> {ptw.revision_notes}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => navigate(`/ptw/edit/${id}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Edit size={18} />
                  Edit PTW
                </button>
                {canEditHighRiskFormsRevision && (
                  <button
                    onClick={() => navigate(`/ptw/${id}/high-risk-forms`)}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                  >
                    <Edit size={18} />
                    Edit High Risk Forms
                  </button>
                )}
                <button
                  onClick={handleResubmit}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                >
                  <Send size={18} />
                  Resubmit ke Approver
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons - HIGH RISK FORMS MODE */}
          {canFillHighRiskForms && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-yellow-800 font-medium">
                  📝 PTW ini membutuhkan pengisian form high risk detail
                </p>
                <button
                  onClick={() => navigate(`/ptw/${id}/high-risk-forms`)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                >
                  <Edit size={18} />
                  Isi High Risk Forms
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons - CLOSING MODE (Vendor submits closing) */}
          {canSubmitClosing && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-800 font-medium">
                    ✅ Semua approval selesai! Silakan submit closing setelah pekerjaan selesai.
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/ptw/${id}/closing`)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckCircle size={18} />
                  Submit Closing
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons - CLOSING APPROVAL MODE (PIC approves closing) */}
          {canApproveClosing && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-purple-800 font-medium">
                  📋 Closing PTW menunggu approval dari Anda
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setActionType('closing_approve');
                      setShowActionModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle size={18} />
                    Approve Closing
                  </button>
                  <button
                    onClick={() => {
                      setActionType('closing_revise');
                      setShowActionModal(true);
                    }}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                  >
                    <Edit size={18} />
                    Revise Closing
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons - PIC SELECT APPROVERS */}
          {canSelectApprovers && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800 font-medium">
                  👥 Sebagai PIC, silakan pilih approvers terlebih dahulu sebelum approve
                </p>
                <button
                  onClick={() => setShowSelectApproversModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Pilih Approvers
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons - APPROVAL MODE (for PIC after selecting approvers, Area Manager, SHE, Management) */}
          {canTakeApprovalAction && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800 font-medium">
                  ✅ PTW ini menunggu approval dari Anda sebagai {ptw.current_approver_role?.replace(/_/g, ' ').toUpperCase()}
                </p>
                <div className="flex gap-2">
                  {canSelectApprovers ? null : (
                    <>
                      <button
                        onClick={() => {
                          setActionType('approve');
                          setShowActionModal(true);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <CheckCircle size={18} />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setActionType('revise');
                          setShowActionModal(true);
                        }}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                      >
                        <Edit size={18} />
                        Revise
                      </button>
                      <button
                        onClick={() => {
                          setActionType('reject');
                          setShowActionModal(true);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                      >
                        <XCircle size={18} />
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Revision Notes */}
          {ptw.status === 'needs_revision' && ptw.revision_notes && !canEdit && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h3 className="text-sm font-semibold text-orange-900 mb-2">📝 Catatan Revisi</h3>
              <p className="text-sm text-orange-800">{ptw.revision_notes}</p>
              <p className="text-xs text-orange-600 mt-2">
                Diminta oleh: {ptw.revised_by_step?.replace(/_/g, ' ').toUpperCase()}
              </p>
            </div>
          )}

          {/* Main Content */}
          <div className="space-y-6">
            {/* Section 1: Info Umum */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText size={20} />
                Informasi Umum
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Lokasi Pekerjaan</label>
                  <p className="text-gray-800">{ptw.lokasi_pekerjaan}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Tanggal Mulai</label>
                  <p className="text-gray-800">{ptw.tanggal_mulai}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-600">Deskripsi Pekerjaan</label>
                  <p className="text-gray-800">{ptw.deskripsi_pekerjaan}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Nama Pekerja</label>
                  <ul className="list-disc list-inside">
                    {ptw.nama_pekerja?.map((nama, idx) => (
                      <li key={idx} className="text-gray-800">{nama}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Kontak</label>
                  <p className="text-gray-800">Phone 1: {ptw.mobile_phone_1}</p>
                  <p className="text-gray-800">Phone 2: {ptw.mobile_phone_2}</p>
                  <p className="text-gray-800">Email: {ptw.requester_email}</p>
                </div>
                {ptw.nomor_po && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Nomor PO</label>
                    <p className="text-gray-800">{ptw.nomor_po}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-600">Requester</label>
                  <p className="text-gray-800">{ptw.requester_name}</p>
                </div>
              </div>
            </div>

            {/* Section 2: High Risk Activities & JSA */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">High Risk Activities & JSA</h2>
              
              {ptw.high_risk_activities && ptw.high_risk_activities.length > 0 && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-600">High Risk Activities</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ptw.high_risk_activities.map((activity, idx) => (
                      <span key={idx} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                        ⚠️ {activity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {ptw.jsa_table && ptw.jsa_table.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Job Safety Analysis</label>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 p-2 text-left text-sm">Langkah Aktifitas</th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Bahaya</th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Risiko</th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Pengendalian Risiko</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ptw.jsa_table.map((row, idx) => (
                          <tr key={idx}>
                            <td className="border border-gray-300 p-2 text-sm">{row.steps_of_activity}</td>
                            <td className="border border-gray-300 p-2 text-sm">{row.hazard_reaction}</td>
                            <td className="border border-gray-300 p-2 text-sm">{row.safe_plan}</td>
                            <td className="border border-gray-300 p-2 text-sm">{row.resources}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Section 3: High Risk Forms Detail */}
            {ptw.high_risk_forms && Object.values(ptw.high_risk_forms).some(form => form !== null) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">High Risk Forms Selected</h2>

                {/* Working at Height */}
                {ptw.high_risk_forms.working_at_height && (
                  <div className="mb-6 p-4 border border-gray-300 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">WORKING AT HEIGHT PERMIT</h3>
                    <p className="text-sm text-gray-600">Scroll kebawah untuk detail</p>
                  </div>
                )}

                {/* Hot Work */}
                {ptw.high_risk_forms.hot_work && (
                  <div className="mb-6 p-4 border border-gray-300 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">HOT WORK PERMIT</h3>
                    <p className="text-sm text-gray-600">Scroll kebawah untuk detail</p>
                  </div>
                )}

                {/* Electric Medium Voltage & Confined Space - Display if exists */}
                {ptw.high_risk_forms.electric_medium_voltage && (
                  <div className="mb-6 p-4 border border-gray-300 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">ELECTRIC MEDIUM VOLTAGE PERMIT</h3>
                    <p className="text-sm text-gray-600">Scroll kebawah untuk detail</p>
                  </div>
                )}

                {ptw.high_risk_forms.confined_space && (
                  <div className="mb-6 p-4 border border-gray-300 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">CONFINED SPACE PERMIT</h3>
                    <p className="text-sm text-gray-600">Scroll kebawah untuk detail</p>
                  </div>
                )}
              </div>
            )}

            {/* Section 4: Bahaya & APD */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Potensi Bahaya & APD</h2>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Potensi Bahaya</label>
                  {ptw.potensi_bahaya && ptw.potensi_bahaya.length > 0 ? (
                    <ul className="list-disc list-inside text-sm text-gray-800">
                      {ptw.potensi_bahaya.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Tidak ada</p>
                  )}
                </div>

                {/* EVALUASI 3: Resiko Lalu Lintas */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Resiko Lalu Lintas</label>
                  {ptw.risiko_lalu_lintas && Object.keys(ptw.risiko_lalu_lintas).filter(k => ptw.risiko_lalu_lintas[k]).length > 0 ? (
                    <ul className="list-disc list-inside text-sm text-gray-800">
                      {Object.keys(ptw.risiko_lalu_lintas).filter(k => ptw.risiko_lalu_lintas[k]).map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Tidak ada</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">APD yang Digunakan</label>
                  {ptw.apd_digunakan && ptw.apd_digunakan.length > 0 ? (
                    <ul className="list-disc list-inside text-sm text-gray-800">
                      {ptw.apd_digunakan.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Tidak ada</p>
                  )}
                </div>

                {/* EVALUASI 3: Personal Hygiene */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Personal Hygiene</label>
                  {ptw.personal_hygiene && Object.keys(ptw.personal_hygiene).filter(k => ptw.personal_hygiene[k]).length > 0 ? (
                    <ul className="list-disc list-inside text-sm text-gray-800">
                      {Object.keys(ptw.personal_hygiene).filter(k => ptw.personal_hygiene[k]).map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Tidak ada</p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 5: Isolasi (EVALUASI 3: Complete) */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Isolasi</h2>
              
              {/* Isolasi Listrik */}
              {ptw.isolasi_listrik?.dilakukan && (
                <div className="mb-4 p-3 border border-gray-300 rounded">
                  <h3 className="font-semibold text-gray-800 mb-2">Isolasi Listrik</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Nama/Nomor Panel:</span> {ptw.isolasi_listrik.nama_nomor_panel || '-'}</p>
                    <p><span className="font-medium">Cara Mengisolasi:</span></p>
                    <ul className="list-disc list-inside ml-4">
                      {ptw.isolasi_listrik.cara_mengisolasi?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                      {ptw.isolasi_listrik.cara_mengisolasi_lainnya && (
                        <li>Lainnya: {ptw.isolasi_listrik.cara_mengisolasi_lainnya}</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Isolasi Utilitas */}
              {ptw.isolasi_utilitas?.dilakukan && (
                <div className="mb-4 p-3 border border-gray-300 rounded">
                  <h3 className="font-semibold text-gray-800 mb-2">Isolasi Utilitas</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Jenis Utilitas:</span></p>
                    <ul className="list-disc list-inside ml-4">
                      {ptw.isolasi_utilitas.jenis_utilitas?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                      {ptw.isolasi_utilitas.jenis_utilitas_lainnya && (
                        <li>Lainnya: {ptw.isolasi_utilitas.jenis_utilitas_lainnya}</li>
                      )}
                    </ul>
                    <p><span className="font-medium">Cara Mengisolasi:</span></p>
                    <ul className="list-disc list-inside ml-4">
                      {ptw.isolasi_utilitas.cara_mengisolasi?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                      {ptw.isolasi_utilitas.cara_mengisolasi_lainnya && (
                        <li>Lainnya: {ptw.isolasi_utilitas.cara_mengisolasi_lainnya}</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Isolasi Proses */}
              {ptw.isolasi_proses?.dilakukan && (
                <div className="mb-4 p-3 border border-gray-300 rounded">
                  <h3 className="font-semibold text-gray-800 mb-2">Isolasi Proses</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Peralatan:</span> {ptw.isolasi_proses.peralatan || '-'}</p>
                    <p><span className="font-medium">Cara Mengisolasi:</span></p>
                    <ul className="list-disc list-inside ml-4">
                      {ptw.isolasi_proses.cara_mengisolasi?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                      {ptw.isolasi_proses.cara_mengisolasi_lainnya && (
                        <li>Lainnya: {ptw.isolasi_proses.cara_mengisolasi_lainnya}</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {!ptw.isolasi_listrik?.dilakukan && !ptw.isolasi_utilitas?.dilakukan && !ptw.isolasi_proses?.dilakukan && (
                <p className="text-sm text-gray-500">Tidak ada isolasi</p>
              )}
            </div>

            {/* Section: Daftar Pencegahan */}
            {(ptw.daftar_pencegahan_umum?.length > 0 || ptw.daftar_pencegahan_penggalian?.length > 0 || ptw.daftar_pencegahan_lainnya?.length > 0) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Daftar Pencegahan</h2>
                
                {ptw.daftar_pencegahan_umum?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Pencegahan Umum:</h3>
                    <ul className="list-disc list-inside text-sm ml-4">
                      {ptw.daftar_pencegahan_umum.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {ptw.daftar_pencegahan_penggalian?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Pencegahan Galian:</h3>
                    <ul className="list-disc list-inside text-sm ml-4">
                      {ptw.daftar_pencegahan_penggalian.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {ptw.daftar_pencegahan_lainnya?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Pencegahan Lainnya:</h3>
                    <ul className="list-disc list-inside text-sm ml-4">
                      {ptw.daftar_pencegahan_lainnya.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Section: High Risk Forms Detail - Using Component */}
            {ptw.high_risk_forms && (ptw.high_risk_forms.working_at_height || ptw.high_risk_forms.electric_medium_voltage || ptw.high_risk_forms.hot_work || ptw.high_risk_forms.confined_space) && (
              <HighRiskFormsDetail highRiskForms={ptw.high_risk_forms} />
            )}

            {/* Section: Closing Details */}
            {(ptw.status === 'waiting_closing_approval' || ptw.status === 'closed') && ptw.closing_submitted && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Closing Details</h2>
                
                <div className="mb-4">
                  <h3 className="font-medium text-gray-700 mb-2">Closing Checklist:</h3>
                  <div className="space-y-2 text-sm">
                    {ptw.closing_checklist && typeof ptw.closing_checklist === 'object' && Object.keys(ptw.closing_checklist).map((key) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className={ptw.closing_checklist[key] ? 'text-green-600' : 'text-gray-400'}>
                          {ptw.closing_checklist[key] ? '✓' : '○'}
                        </span>
                        <span className="text-gray-700">{key.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {ptw.closing_notes && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Closing Notes:</h3>
                    <p className="text-sm text-gray-800 p-3 bg-gray-50 rounded">{ptw.closing_notes}</p>
                  </div>
                )}
                
                <div className="p-3 bg-purple-50 rounded">
                  <p className="text-sm">
                    <span className="font-medium">Disclaimer Closing: </span>
                    <span className={ptw.closing_disclaimer_accepted ? 'text-green-600' : 'text-red-600'}>
                      {ptw.closing_disclaimer_accepted ? '✓ Disetujui' : '✗ Belum disetujui'}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Section 6: Approval Flow */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Approval Flow</h2>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">1</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">PIC</p>
                    <p className="text-sm text-gray-600">{ptw.pic_name || '-'}</p>
                  </div>
                  {ptw.status === 'waiting_pic' ? (
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Pending</span>
                  ) : ptw.approval_history?.find(h => h.role === 'pic' && h.action === 'approve') ? (
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">Approved</span>
                  ) : null}
                </div>

                {ptw.area_manager_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">2</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">Area Manager</p>
                      <p className="text-sm text-gray-600">{ptw.area_manager_name}</p>
                    </div>
                    {ptw.status === 'waiting_area_manager' ? (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Pending</span>
                    ) : ptw.approval_history?.find(h => h.role === 'area_manager' && h.action === 'approve') ? (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">Approved</span>
                    ) : null}
                  </div>
                )}

                {ptw.she_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">3</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">SHE</p>
                      <p className="text-sm text-gray-600">{ptw.she_name}</p>
                    </div>
                    {ptw.status === 'waiting_she' ? (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Pending</span>
                    ) : ptw.approval_history?.find(h => h.role === 'she' && h.action === 'approve') ? (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">Approved</span>
                    ) : null}
                  </div>
                )}

                {ptw.management_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">4</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">Leadership Team</p>
                      <p className="text-sm text-gray-600">{ptw.management_name}</p>
                    </div>
                    {ptw.status === 'waiting_management' ? (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Pending</span>
                    ) : ptw.approval_history?.find(h => h.role === 'management' && h.action === 'approve') ? (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">Approved</span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* Section 7: Approval History */}
            {ptw.approval_history && ptw.approval_history.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Approval History</h2>
                <div className="space-y-3">
                  {ptw.approval_history.map((history, idx) => (
                    <div key={idx} className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{history.user_name}</p>
                          <p className="text-xs text-gray-600">{history.role?.replace(/_/g, ' ').toUpperCase()}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded ${
                            history.action === 'approve' ? 'bg-green-100 text-green-800' :
                            history.action === 'revise' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {history.action.toUpperCase()}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                          {new Date(history.timestamp + 'Z').toLocaleString('id-ID', {
                            timeZone: 'Asia/Jakarta',
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                          </p>
                        </div>
                      </div>
                      {history.notes && (
                        <p className="text-sm text-gray-700 mt-2">{history.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {actionType === 'approve' ? 'Approve PTW' : actionType === 'revise' ? 'Request Revision' : 'Reject PTW'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catatan {actionType === 'revise' ? '(Required)' : '(Optional)'}
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
                rows="4"
                placeholder={`Tuliskan catatan ${actionType}...`}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setActionNotes('');
                  setActionType('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Batal
              </button>
              <button
                onClick={handleAction}
                disabled={actionLoading}
                className={`flex-1 px-4 py-2 text-white rounded-lg ${
                  actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  actionType === 'revise' ? 'bg-orange-600 hover:bg-orange-700' :
                  'bg-red-600 hover:bg-red-700'
                } disabled:bg-gray-400`}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Approvers Modal */}
      {showSelectApproversModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pilih Approvers</h3>
            
            <div className="space-y-4 mb-6">

            {/* 🔴 TAMBAHAN 1: Peringatan Amonia 🔴 */}
              {ptw?.high_risk_activities?.includes('Amonia') && (
                <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Perhatian:</strong> Karena PTW ini memiliki aktivitas <strong>Ammonia</strong>, 
                    approval Manajemen <strong>WAJIB</strong> dipilih.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Area Manager *</label>
                <select
                  value={selectedApprovers.area_manager_id}
                  onChange={(e) => setSelectedApprovers({ ...selectedApprovers, area_manager_id: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                >
                  <option value="">-- Pilih Area Manager --</option>
                  {areaManagerUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SHE *</label>
                <select
                  value={selectedApprovers.she_id}
                  onChange={(e) => setSelectedApprovers({ ...selectedApprovers, she_id: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                >
                  <option value="">-- Pilih SHE --</option>
                  {sheUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))}
                </select>
              </div>

               {/* 🔴 TAMBAHAN 2: Modifikasi bagian Leadership Team 🔴 */}
                                          {/* 🔴 PERBAIKAN: Tampilkan Leadership Team jika ADA High Risk ATAU ADA Amonia */}
              {((ptw.high_risk_activities && ptw.high_risk_activities.length > 0) || 
                (ptw?.isolasi_utilitas?.dilakukan && ptw?.isolasi_utilitas?.jenis_utilitas?.includes('Amonia'))) && (
                <div>
                  {/* 🔴 PERBAIKAN: Cek Amonia dari isolasi_utilitas untuk menentukan wajib/tidak */}
                  {(() => {
                    const hasAmonia = ptw?.isolasi_utilitas?.dilakukan && 
                                      ptw?.isolasi_utilitas?.jenis_utilitas?.includes('Amonia');
                    return (
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Leadership Team 
                        {hasAmonia ? 
                          <span className="text-red-500">* (WAJIB untuk Amonia)</span> : 
                          <span className="text-gray-500"> (Opsional - untuk High Risk Activities)</span>}
                      </label>
                    );
                  })()}
                  <select
                    value={selectedApprovers.management_id}
                    onChange={(e) => setSelectedApprovers({ ...selectedApprovers, management_id: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    {(() => {
                      const hasAmonia = ptw?.isolasi_utilitas?.dilakukan && 
                                        ptw?.isolasi_utilitas?.jenis_utilitas?.includes('Amonia');
                      return (
                        <option value="">
                          -- Pilih Leadership Team {hasAmonia ? '(WAJIB)' : '(Optional)'} --
                        </option>
                      );
                    })()}
                    {managementUsers.map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSelectApproversModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Batal
              </button>
              <button
                onClick={handleSelectApprovers}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {actionLoading ? 'Processing...' : 'Simpan Approvers'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PTWDetail;
